const DB_NAME = 'plugkit-wasm'
const DB_VERSION = 1
const STORES = ['kv', 'vec', 'tasks', 'keys']
const OPFS_THRESHOLD = 1024 * 1024

let dbPromise = null

export function openDB() {
    if (dbPromise) return dbPromise
    dbPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION)
        req.onupgradeneeded = () => {
            const db = req.result
            for (const name of STORES) {
                if (!db.objectStoreNames.contains(name)) {
                    db.createObjectStore(name)
                }
            }
        }
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error)
    })
    return dbPromise
}

function tx(db, store, mode) {
    return db.transaction(store, mode).objectStore(store)
}

async function opfsRoot() {
    if (!navigator?.storage?.getDirectory) throw new Error('OPFS unavailable')
    return await navigator.storage.getDirectory()
}

async function opfsWrite(name, data) {
    const root = await opfsRoot()
    const handle = await root.getFileHandle(name, { create: true })
    const w = await handle.createWritable()
    await w.write(data)
    await w.close()
}

async function opfsRead(name) {
    const root = await opfsRoot()
    const handle = await root.getFileHandle(name)
    const file = await handle.getFile()
    return new Uint8Array(await file.arrayBuffer())
}

async function opfsDelete(name) {
    try {
        const root = await opfsRoot()
        await root.removeEntry(name)
    } catch {}
}

function compositeKey(ns, key) { return ns + ' ' + key }

export async function kvGet(ns, key) {
    const db = await openDB()
    return await new Promise((resolve, reject) => {
        const r = tx(db, 'kv', 'readonly').get(compositeKey(ns, key))
        r.onsuccess = async () => {
            const val = r.result
            if (val && val.__opfs) {
                try { resolve(await opfsRead(val.__opfs)) } catch (e) { reject(e) }
            } else resolve(val ?? null)
        }
        r.onerror = () => reject(r.error)
    })
}

export async function kvPut(ns, key, value) {
    const db = await openDB()
    const ck = compositeKey(ns, key)
    let stored = value
    const byteLen = value?.byteLength ?? (value instanceof ArrayBuffer ? value.byteLength : (value && value.length) || 0)
    if (byteLen > OPFS_THRESHOLD) {
        const fname = 'kv-' + btoa(ck).replace(/[^A-Za-z0-9]/g, '_')
        await opfsWrite(fname, value)
        stored = { __opfs: fname, size: byteLen }
    }
    return await new Promise((resolve, reject) => {
        const r = tx(db, 'kv', 'readwrite').put(stored, ck)
        r.onsuccess = () => resolve(true)
        r.onerror = () => reject(r.error)
    })
}

export async function kvDelete(ns, key) {
    const db = await openDB()
    const ck = compositeKey(ns, key)
    const existing = await new Promise((resolve, reject) => {
        const r = tx(db, 'kv', 'readonly').get(ck)
        r.onsuccess = () => resolve(r.result)
        r.onerror = () => reject(r.error)
    })
    if (existing && existing.__opfs) await opfsDelete(existing.__opfs)
    return await new Promise((resolve, reject) => {
        const r = tx(db, 'kv', 'readwrite').delete(ck)
        r.onsuccess = () => resolve(true)
        r.onerror = () => reject(r.error)
    })
}

export async function kvQuery(ns, query) {
    const db = await openDB()
    const prefix = ns + ' '
    const needle = (query || '').toLowerCase()
    const out = []
    return await new Promise((resolve, reject) => {
        const store = tx(db, 'kv', 'readonly')
        const range = IDBKeyRange.bound(prefix, prefix + '￿')
        const cur = store.openCursor(range)
        cur.onsuccess = () => {
            const c = cur.result
            if (!c) return resolve(out)
            const key = c.key.slice(prefix.length)
            if (!needle || key.toLowerCase().includes(needle)) {
                out.push({ key, value: c.value })
            }
            c.continue()
        }
        cur.onerror = () => reject(cur.error)
    })
}

export async function vecPut(id, embedding, payload) {
    const db = await openDB()
    return await new Promise((resolve, reject) => {
        const r = tx(db, 'vec', 'readwrite').put({ embedding, payload }, id)
        r.onsuccess = () => resolve(true)
        r.onerror = () => reject(r.error)
    })
}

function cosine(a, b) {
    const n = Math.min(a.length, b.length)
    let dot = 0, na = 0, nb = 0
    for (let i = 0; i < n; i++) { dot += a[i] * b[i]; na += a[i] * a[i]; nb += b[i] * b[i] }
    return dot / (Math.sqrt(na) * Math.sqrt(nb) + 1e-9)
}

export async function vecSearch(queryEmbedding, k) {
    const db = await openDB()
    const hits = []
    return await new Promise((resolve, reject) => {
        const cur = tx(db, 'vec', 'readonly').openCursor()
        cur.onsuccess = () => {
            const c = cur.result
            if (!c) {
                hits.sort((a, b) => b.score - a.score)
                return resolve(hits.slice(0, k))
            }
            const v = c.value
            if (v && v.embedding && queryEmbedding) {
                hits.push({ id: c.key, score: cosine(queryEmbedding, v.embedding), payload: v.payload })
            }
            c.continue()
        }
        cur.onerror = () => reject(cur.error)
    })
}

export async function tasksPut(taskId, record) {
    const db = await openDB()
    return await new Promise((resolve, reject) => {
        const r = tx(db, 'tasks', 'readwrite').put(record, taskId)
        r.onsuccess = () => resolve(true)
        r.onerror = () => reject(r.error)
    })
}

export async function tasksGet(taskId) {
    const db = await openDB()
    return await new Promise((resolve, reject) => {
        const r = tx(db, 'tasks', 'readonly').get(taskId)
        r.onsuccess = () => resolve(r.result ?? null)
        r.onerror = () => reject(r.error)
    })
}

export async function tasksList() {
    const db = await openDB()
    const out = []
    return await new Promise((resolve, reject) => {
        const cur = tx(db, 'tasks', 'readonly').openCursor()
        cur.onsuccess = () => {
            const c = cur.result
            if (!c) return resolve(out)
            out.push({ id: c.key, ...c.value })
            c.continue()
        }
        cur.onerror = () => reject(cur.error)
    })
}

export async function keysGet(provider) {
    const db = await openDB()
    return await new Promise((resolve, reject) => {
        const r = tx(db, 'keys', 'readonly').get(provider)
        r.onsuccess = () => resolve(r.result ?? null)
        r.onerror = () => reject(r.error)
    })
}

export async function keysPut(provider, record) {
    const db = await openDB()
    return await new Promise((resolve, reject) => {
        const r = tx(db, 'keys', 'readwrite').put(record, provider)
        r.onsuccess = () => resolve(true)
        r.onerror = () => reject(r.error)
    })
}

export async function fsRead(path) {
    const root = await opfsRoot()
    const parts = path.replace(/^\/+/, '').split('/').filter(Boolean)
    let dir = root
    for (let i = 0; i < parts.length - 1; i++) {
        dir = await dir.getDirectoryHandle(parts[i])
    }
    const handle = await dir.getFileHandle(parts[parts.length - 1])
    const file = await handle.getFile()
    return new Uint8Array(await file.arrayBuffer())
}

export async function fsWrite(path, data) {
    const root = await opfsRoot()
    const parts = path.replace(/^\/+/, '').split('/').filter(Boolean)
    let dir = root
    for (let i = 0; i < parts.length - 1; i++) {
        dir = await dir.getDirectoryHandle(parts[i], { create: true })
    }
    const handle = await dir.getFileHandle(parts[parts.length - 1], { create: true })
    const w = await handle.createWritable()
    await w.write(data)
    await w.close()
    return true
}

export async function fsReaddir(path) {
    const root = await opfsRoot()
    const parts = path.replace(/^\/+/, '').split('/').filter(Boolean)
    let dir = root
    for (const p of parts) dir = await dir.getDirectoryHandle(p)
    const entries = []
    for await (const [name, handle] of dir.entries()) {
        entries.push({ name, kind: handle.kind })
    }
    return entries
}

export async function fsStat(path) {
    try {
        const root = await opfsRoot()
        const parts = path.replace(/^\/+/, '').split('/').filter(Boolean)
        let dir = root
        for (let i = 0; i < parts.length - 1; i++) dir = await dir.getDirectoryHandle(parts[i])
        const last = parts[parts.length - 1]
        try {
            const fh = await dir.getFileHandle(last)
            const f = await fh.getFile()
            return { kind: 'file', size: f.size, mtime: f.lastModified }
        } catch {
            await dir.getDirectoryHandle(last)
            return { kind: 'directory', size: 0, mtime: 0 }
        }
    } catch (e) {
        return null
    }
}
